import { useState, useRef, useEffect } from "react";
import { z } from "zod";
import { Mail, MapPin, Phone } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Layout, PageHero } from "@/components/site/Layout";
import { LocationMap } from "@/components/site/LocationMap";
import contactHeroImage from "@/assets/atasehir-skyline.webp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/lib/i18n";
import { getSupabase } from "@/integrations/supabase/lazy";
import { Turnstile } from "@/components/site/Turnstile";

const API_BASE = "";
const TURNSTILE_SITE_KEY = "0x4AAAAAADvmhWRkWHFy1Z7F";

async function submitToApi(payload: Record<string, unknown>): Promise<{ error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/api/contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Gönderim başarısız." };
    return {};
  } catch {
    return { error: "Bağlantı hatası. Lütfen tekrar deneyin." };
  }
}
import { Seo } from "@/components/site/Seo";
import { organizationJsonLd, ORGANIZATION_ID, WEBSITE_ID } from "@/lib/seoSchemas";
import { pick } from "@/lib/i18n";
import { COPY } from "@/lib/uiCopy";
import { fetchSettings, fetchServices } from "@/lib/queries";
import { fallbackServices, mergeServiceFallbacks } from "@/lib/serviceData";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const contactSchema = z.object({
  full_name: z.string().trim().min(2).max(120),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().max(40).optional().or(z.literal("")),
  company: z.string().trim().max(120).optional().or(z.literal("")),
  service_interest: z.string().max(120).optional().or(z.literal("")),
  message: z.string().trim().min(5).max(4000),
});

const hrSchema = z.object({
  full_name: z.string().trim().min(2).max(120),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().max(40).optional().or(z.literal("")),
  position: z.string().trim().max(120).optional().or(z.literal("")),
  message: z.string().trim().max(4000).optional().or(z.literal("")),
  cv_url: z.string().trim().max(500).optional().or(z.literal("")),
});

function FormShell({ children }: { children: React.ReactNode }) {
  return <div className="rounded-2xl bg-card shadow-card p-6 md:p-8 border border-border">{children}</div>;
}

export function ContactPage() {
  const { t, locale } = useI18n();
  const { toast } = useToast();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: fetchSettings });
  const get = (k: string) => {
    const s = settings?.get(k);
    return s ? (s[`value_${locale}`] || s.value_tr || s.value_en) || "" : "";
  };
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const mountedAtRef = useRef<number>(Date.now());
  useEffect(() => { mountedAtRef.current = Date.now(); }, []);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);

    // Honeypot — bot doldurursa sessizce başarı göster
    if ((fd.get("website") as string)?.trim()) {
      toast({ title: t("form.success") });
      (e.target as HTMLFormElement).reset();
      return;
    }

    // Minimum süre kontrolü — bot anında submit etmesin (3 sn)
    if (Date.now() - mountedAtRef.current < 3000) {
      toast({ title: t("form.error"), description: pick(locale, COPY.pleaseWaitSubmit), variant: "destructive" });
      return;
    }

    // Rate limit — aynı tarayıcıdan 60 sn'de bir gönderim
    const last = Number(localStorage.getItem("contact_last_submit") || "0");
    if (Date.now() - last < 60_000) {
      const wait = Math.ceil((60_000 - (Date.now() - last)) / 1000);
      toast({ title: t("form.error"), description: pick(locale, COPY.rateLimit).replace("{wait}", String(wait)), variant: "destructive" });
      return;
    }

    const data = Object.fromEntries(fd);
    delete (data as any).website;
    const parsed = contactSchema.safeParse(data);
    if (!parsed.success) {
      toast({ title: t("form.error"), description: parsed.error.issues[0]?.message, variant: "destructive" });
      return;
    }
    setLoading(true);
    const payload = { ...parsed.data, type: "contact", cf_turnstile_token: turnstileToken } as any;
    const { error } = await submitToApi(payload);
    setLoading(false);
    if (error) {
      toast({ title: t("form.error"), description: error, variant: "destructive" });
      setTurnstileToken(null);
      setTurnstileResetKey((k) => k + 1);
    } else {
      localStorage.setItem("contact_last_submit", String(Date.now()));
      toast({ title: t("form.success") });
      (e.target as HTMLFormElement).reset();
      mountedAtRef.current = Date.now();
      setTurnstileToken(null);
      setTurnstileResetKey((k) => k + 1);
    }
  }

  const lat = parseFloat(get("map_lat") || "40.984639");
  const lng = parseFloat(get("map_lng") || "29.136611");
  const address = get("address_tr") || "Anadolu Terra";
  const directionsHref = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

  return (
    <Layout>
      <Seo
        path="/iletisim"
        title={t("nav.contact")}
        description={pick(locale, COPY.contactSeoDesc)}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "@id": "https://anadoluterra.com/iletisim#contactpage",
            name: t("nav.contact"),
            url: "https://anadoluterra.com/iletisim",
            inLanguage: locale,
            isPartOf: { "@id": WEBSITE_ID },
            about: { "@id": ORGANIZATION_ID },
            mainEntity: organizationJsonLd({
              lat,
              lng,
              address: get("address_tr") || "Atatürk Mahallesi Mustafa Kemal Caddesi No:1 Kat:1",
            }),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://anadoluterra.com/" },
              { "@type": "ListItem", position: 2, name: t("nav.contact"), item: "https://anadoluterra.com/iletisim" },
            ],
          },
        ]}
      />
      <PageHero title={t("nav.contact")} subtitle={get("address_tr")} image={contactHeroImage} overlay="light" />

      {/* HARITA — Google Maps embed */}
      <section className="pt-12 md:pt-16">
        <div className="container-page">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-semibold text-primary relative pb-2 after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-0.5 after:w-12 after:bg-accent">
              Google Maps
            </h2>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md bg-card border border-border hover:bg-accent hover:text-accent-foreground px-3 py-1.5 text-sm font-medium transition-base shadow-sm"
            >
              {pick(locale, COPY.openInMaps)}
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </a>
          </div>
          <div className="rounded-2xl overflow-hidden border border-border shadow-card">
            <iframe
              title="Google Maps - Anadolu Terra"
              src={`https://maps.google.com/maps?q=${lat},${lng}&hl=${locale}&z=15&output=embed`}
              width="100%"
              height="460"
              style={{ border: 0, display: "block" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <div className="mt-3 flex items-start gap-2.5 text-sm text-foreground/80">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-accent" />
            <span>{address}</span>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-page grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-4">
            {[
              { icon: Phone, label: t("contact.phone"), value: get("phone") },
              { icon: Mail, label: t("contact.email"), value: get("email") },
              { icon: MapPin, label: t("contact.address"), value: get("address_tr") },
            ].map((c) => (
              <div key={c.label} className="rounded-2xl border border-border bg-card p-4 md:p-5 shadow-card flex items-center gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
                  <c.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</div>
                  <div className="mt-1 text-sm font-medium text-foreground break-words">{c.value}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="lg:col-span-2" id="form">
            <FormShell>
              <h2 className="font-display text-2xl font-semibold text-primary mb-6">{t("nav.contact")}</h2>
              <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4">
                {/* Honeypot — botlar için görünmez tuzak alan */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute left-[-9999px] h-0 w-0 opacity-0"
                />
                <div><Label htmlFor="contact-full_name">{t("form.fullName")}</Label><Input id="contact-full_name" name="full_name" required maxLength={120} /></div>
                <div><Label htmlFor="contact-email">{t("form.email")}</Label><Input id="contact-email" name="email" type="email" required maxLength={200} /></div>
                <div><Label htmlFor="contact-phone">{t("form.phone")}</Label><Input id="contact-phone" name="phone" maxLength={40} /></div>
                <div><Label htmlFor="contact-company">{t("form.company")}</Label><Input id="contact-company" name="company" maxLength={120} /></div>
                <div className="sm:col-span-2"><Label htmlFor="contact-message">{t("form.message")}</Label><Textarea id="contact-message" name="message" required maxLength={4000} rows={5} /></div>
                <div className="sm:col-span-2 flex flex-col gap-2">
                  <Turnstile sitekey={TURNSTILE_SITE_KEY} onToken={setTurnstileToken} resetKey={turnstileResetKey} />
                  <Button type="submit" disabled={loading || !turnstileToken} className="bg-accent text-accent-foreground hover:bg-accent/90 w-full">
                    {loading ? t("common.sending") : t("common.send")}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">Bu form Cloudflare Turnstile ile korunmaktadır.</p>
                </div>
              </form>
            </FormShell>
          </div>
        </div>
      </section>

    </Layout>
  );
}

export function QuotePage() {
  const { t, locale } = useI18n();
  const { toast } = useToast();
  const { data: remoteServices } = useQuery({ queryKey: ["services"], queryFn: fetchServices, retry: false });
  // Uzak kaynak boş/erişilemez olduğunda liste boş kalmasın — yerleşik hizmetlere düş.
  const services = (remoteServices && remoteServices.length > 0)
    ? mergeServiceFallbacks(remoteServices as any[])
    : (fallbackServices as any[]);
  const [loading, setLoading] = useState(false);
  const [serviceInterest, setServiceInterest] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const obj = Object.fromEntries(fd) as any;
    obj.service_interest = serviceInterest;
    const parsed = contactSchema.safeParse(obj);
    if (!parsed.success) {
      toast({ title: t("form.error"), description: parsed.error.issues[0]?.message, variant: "destructive" });
      return;
    }
    setLoading(true);
    const payload = { ...parsed.data, type: "quote", full_name: (parsed.data as any).full_name ?? (parsed.data as any).name, cf_turnstile_token: turnstileToken } as any;
    const { error } = await submitToApi(payload);
    setLoading(false);
    if (error) {
      toast({ title: t("form.error"), description: error, variant: "destructive" });
      setTurnstileToken(null);
      setTurnstileResetKey((k) => k + 1);
    } else {
      toast({ title: t("form.success") });
      (e.target as HTMLFormElement).reset();
      setServiceInterest("");
      setTurnstileToken(null);
      setTurnstileResetKey((k) => k + 1);
    }
  }

  return (
    <Layout>
      <Seo
        path="/teklif-iste"
        title={t("quote.title")}
        description={t("quote.subtitle")}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://anadoluterra.com/" },
              { "@type": "ListItem", position: 2, name: t("quote.title"), item: "https://anadoluterra.com/teklif-iste" },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://anadoluterra.com/teklif-iste#webpage",
            name: t("quote.title"),
            description: t("quote.subtitle"),
            url: "https://anadoluterra.com/teklif-iste",
            inLanguage: locale,
            isPartOf: { "@id": "https://anadoluterra.com/#website" },
            about: { "@id": "https://anadoluterra.com/#organization" },
            potentialAction: {
              "@type": "RequestQuoteAction",
              target: "https://anadoluterra.com/teklif-iste",
            },
          },
        ]}
      />
      <PageHero title={t("quote.title")} subtitle={t("quote.subtitle")} variant="band" />
      <section className="section-padding">
        <div className="container-page max-w-3xl">
          <FormShell>
            <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4">
              <div><Label htmlFor="quote-full_name">{t("form.fullName")}</Label><Input id="quote-full_name" name="full_name" required maxLength={120} /></div>
              <div><Label htmlFor="quote-email">{t("form.email")}</Label><Input id="quote-email" name="email" type="email" required maxLength={200} /></div>
              <div><Label htmlFor="quote-phone">{t("form.phone")}</Label><Input id="quote-phone" name="phone" maxLength={40} /></div>
              <div><Label htmlFor="quote-company">{t("form.company")}</Label><Input id="quote-company" name="company" maxLength={120} /></div>
              <div className="sm:col-span-2">
                <Label htmlFor="quote-service_interest">{t("form.serviceInterest")}</Label>
                <Select value={serviceInterest} onValueChange={setServiceInterest}>
                  <SelectTrigger id="quote-service_interest"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {services?.map((s) => (
                      <SelectItem key={s.id} value={s.slug}>
                        {s[`title_${locale}`] || s.title_en || s.title_tr}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label htmlFor="quote-message">{t("form.message")}</Label><Textarea id="quote-message" name="message" required maxLength={4000} rows={5} /></div>
              <div className="sm:col-span-2 flex flex-col gap-2">
                <Turnstile sitekey={TURNSTILE_SITE_KEY} onToken={setTurnstileToken} resetKey={turnstileResetKey} />
                <Button type="submit" disabled={loading || !turnstileToken} className="bg-accent text-accent-foreground hover:bg-accent/90 w-full">
                  {loading ? t("common.sending") : t("common.requestQuote")}
                </Button>
                <p className="text-xs text-muted-foreground text-center">Bu form Cloudflare Turnstile ile korunmaktadır.</p>
              </div>
            </form>
          </FormShell>
        </div>
      </section>
    </Layout>
  );
}

export function HrPage() {
  const { t, locale } = useI18n();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const parsed = hrSchema.safeParse(Object.fromEntries(fd));
    if (!parsed.success) {
      toast({ title: t("form.error"), description: parsed.error.issues[0]?.message, variant: "destructive" });
      return;
    }
    setLoading(true);
    const supabase = await getSupabase();
    const { error } = await supabase.from("job_applications").insert([parsed.data as any]);
    setLoading(false);
    if (error) toast({ title: t("form.error"), description: error.message, variant: "destructive" });
    else { toast({ title: t("form.successHr") }); (e.target as HTMLFormElement).reset(); }
  }

  return (
    <Layout>
      <Seo
        path="/insan-kaynaklari"
        title={t("hr.title")}
        description={pick(locale, COPY.hrSeoDesc)}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://anadoluterra.com/" },
              { "@type": "ListItem", position: 2, name: t("hr.title"), item: "https://anadoluterra.com/insan-kaynaklari" },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://anadoluterra.com/insan-kaynaklari#webpage",
            name: t("hr.title"),
            description: pick(locale, COPY.hrSeoDesc),
            url: "https://anadoluterra.com/insan-kaynaklari",
            inLanguage: locale,
            isPartOf: { "@id": "https://anadoluterra.com/#website" },
            about: { "@id": "https://anadoluterra.com/#organization" },
          },
        ]}
      />
      <PageHero title={t("hr.title")} subtitle={t("hr.subtitle")} variant="band" />
      <section className="section-padding">
        <div className="container-page max-w-3xl">
          <FormShell>
            <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4">
              <div><Label htmlFor="hr-full_name">{t("form.fullName")}</Label><Input id="hr-full_name" name="full_name" required maxLength={120} /></div>
              <div><Label htmlFor="hr-email">{t("form.email")}</Label><Input id="hr-email" name="email" type="email" required maxLength={200} /></div>
              <div><Label htmlFor="hr-phone">{t("form.phone")}</Label><Input id="hr-phone" name="phone" maxLength={40} /></div>
              <div><Label htmlFor="hr-position">{t("form.position")}</Label><Input id="hr-position" name="position" maxLength={120} /></div>
              <div className="sm:col-span-2"><Label htmlFor="hr-cv_url">{t("form.cv")}</Label><Input id="hr-cv_url" name="cv_url" maxLength={500} placeholder="https://..." /></div>
              <div className="sm:col-span-2"><Label htmlFor="hr-message">{t("form.message")}</Label><Textarea id="hr-message" name="message" maxLength={4000} rows={5} /></div>
              <Button type="submit" disabled={loading} className="bg-accent text-accent-foreground hover:bg-accent/90 sm:col-span-2">
                {loading ? t("common.sending") : t("common.send")}
              </Button>
            </form>
          </FormShell>
        </div>
      </section>
    </Layout>
  );
}
